import React from 'react';
import { Copy, Save } from 'lucide-react';
import AdminShell from '../../admin/components/layout/AdminShell';
import AdminPageHeader from '../../admin/components/shared/AdminPageHeader';
import AdminPanel from '../../admin/components/shared/AdminPanel';
import SecondaryActionButton from '../../admin/components/buttons/SecondaryActionButton';
import FileUploadField from '../../admin/components/fields/FileUploadField';
import EmptyStatePanel from '../../admin/components/shared/EmptyStatePanel';
import TextField from '../../admin/components/fields/TextField';
import SmartImagePreview from '../../components/shared/SmartImagePreview';
import { useAdminContext } from '../../context/AdminContext';

const AdminMediaPage: React.FC = () => {
  const { draftWorkspace, draftStatus, draftError, saveDraft, saving, uploadImage, updateWorkspace } =
    useAdminContext();

  const handleCopy = async (url: string) => {
    await navigator.clipboard.writeText(url);
  };

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow="Media Library"
        title="Upload and reuse food or category images"
        description="All uploaded images are saved to shared storage and can be reused from item, category, logo, favicon, or settings editors."
        actions={
          <SecondaryActionButton onClick={() => void saveDraft()} disabled={saving || draftStatus !== 'ready'}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Draft'}
          </SecondaryActionButton>
        }
      />

      {draftError && (
        <AdminPanel className="p-4">
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {draftError}
          </div>
        </AdminPanel>
      )}

      {!draftWorkspace || draftStatus === 'loading' ? (
        <AdminPanel className="p-8 text-center text-white/60">Loading media library...</AdminPanel>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <FileUploadField
              label="Food Images"
              description="Upload dish photos that can be attached to individual menu items."
              onUpload={(file) => uploadImage(file, 'item-image', file.name)}
            />
            <FileUploadField
              label="Category and Cover Images"
              description="Upload category artwork, menu photography, logos, and the main restaurant cover image."
              onUpload={(file) => uploadImage(file, 'category-cover', file.name)}
            />
            <FileUploadField
              label="Branding Images"
              description="Upload restaurant logos, favicon icons, and other brand graphics."
              onUpload={(file) => uploadImage(file, 'branding-image', file.name)}
            />
          </div>

          {draftWorkspace.mediaAssets.length === 0 ? (
            <EmptyStatePanel title="No uploaded media yet" description="Upload your first image from the browser to start building the shared media library." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {draftWorkspace.mediaAssets.map((asset) => (
                <AdminPanel key={asset.id} className="overflow-hidden p-0">
                  <SmartImagePreview
                    src={asset.url}
                    alt={asset.altText || asset.kind}
                    className="h-52 rounded-none border-0"
                  />
                  <div className="space-y-4 p-5">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">{asset.kind}</div>
                    <TextField
                      label="Image name / alt text"
                      value={asset.altText ?? ''}
                      onChange={(value) =>
                        updateWorkspace((workspace) => ({
                          ...workspace,
                          mediaAssets: workspace.mediaAssets.map((existing) =>
                            existing.id === asset.id ? { ...existing, altText: value } : existing,
                          ),
                        }))
                      }
                      helpText="Used for accessibility and labels in the media library."
                    />
                    <div className="text-xs text-white/40">{new Date(asset.createdAt).toLocaleString()}</div>
                    <div className="flex gap-3">
                      <SecondaryActionButton onClick={() => void handleCopy(asset.url)}>
                        <Copy className="h-4 w-4" />
                        Copy URL
                      </SecondaryActionButton>
                    </div>
                  </div>
                </AdminPanel>
              ))}
            </div>
          )}
        </>
      )}
    </AdminShell>
  );
};

export default AdminMediaPage;
